import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";

function GetCategory() {
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const limit = 20;
  const navigate = useNavigate();

  useEffect(() => {
    fetch("https://visitguru.in/api/v1/category/getCategory")
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setCategories(data.data);
      })
      .catch((err) => console.error("Error fetching categories:", err));
  }, []);

  const handleDelete = async (categoryId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#11ADA3",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await fetch(
        "https://visitguru.in/api/v1/category/deleteCategory",
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ categoryId }),
        }
      );
      const data = await response.json();

      if (data.success) {
        toast.success("Category deleted successfully!");
        setCategories((prev) => prev.filter((c) => c._id !== categoryId));
      } else {
        toast.error(data.message || "Failed to delete category.");
      }
    } catch (err) {
      console.error("Error deleting category:", err);
      toast.error("Something went wrong!");
    }
  };

  const totalPages = Math.ceil(categories.length / limit);
  const currentCategories = categories.slice((page - 1) * limit, page * limit);

  // PDF Generation function
  const generatePDF = () => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("Category Report", 14, 22);

    const tableColumn = ["#", "Category Name", "Image URL"];
    const tableRows = [];

    categories.forEach((cat, index) => {
      const rowData = [
        index + 1,
        cat.categoryName || "N/A",
        cat.catimg || "N/A",
      ];
      tableRows.push(rowData);
    });

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 30,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [8, 132, 196] },
      theme: "striped",
      didDrawCell: (data) => {
        // If you want to show images in PDF (complex) you could add here with doc.addImage()
        // But for now, just print image URLs as text.
      },
    });

    doc.save("categories-report.pdf");
  };

  return (
    <div className="space-y-12">
      <div className="relative flex flex-col min-w-0 break-words bg-white w-full shadow-lg rounded">
        <div className="rounded-t px-4 py-3 border-0 flex items-center justify-between">
          <h3 className="font-semibold text-base text-blueGray-700 flex-grow">
            Categories{" "}
            <span className="ml-2 text-sm text-blueGray">
              (Total Categories: {categories.length})
            </span>
          </h3>
          <button
            onClick={generatePDF}
            style={{
              backgroundColor: "#0884c4",
              width: "100px",
              height: "35px",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              outline: "none",
              boxShadow: "none",
            }}
          >
            Download
          </button>
        </div>
        <div className="block w-full overflow-x-auto">
          <table className="items-center w-full bg-transparent border-collapse">
            <thead>
              <tr>
                {["#", "Category Name", "Image", "Action"].map((heading, idx) => (
                  <th
                    key={idx}
                    className="px-6 bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100 py-3 text-xs uppercase font-semibold text-left"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentCategories.length > 0 ? (
                currentCategories.map((cat, i) => (
                  <tr key={cat._id}>
                    <td className="border-t-0 px-6 text-xs p-4">
                      {(page - 1) * limit + i + 1}
                    </td>
                    <td className="border-t-0 px-6 text-xs p-4">{cat.categoryName}</td>
                    <td className="border-t-0 px-6 text-xs p-4">
                      <img
                        src={cat.catimg}
                        alt={cat.categoryName}
                        className="w-12 h-12 object-contain"
                      />
                    </td>
                    <td className="border-t-0 px-6 text-xs p-4">
                      <div className="flex items-center space-x-2">
                        {/* Edit */}
                        <button
                          onClick={() =>
                            navigate(`/admin/editcategory/${cat._id}`, { state: cat })
                          }
                          className="p-2 rounded-full hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-200 transition"
                          aria-label="Edit category"
                        >
                          <i className="fas fa-edit text-green-500 text-lg" />
                        </button>
                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(cat._id)}
                          className="p-2 rounded-full hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-200 transition"
                          aria-label="Delete category"
                        >
                          <i className="fas fa-trash-alt text-red-500 text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center p-4">
                    No categories found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {categories.length > limit && (
          <div className="flex justify-between items-center px-6 py-4">
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={page === 1}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              disabled={page === totalPages}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default GetCategory;